/**
 * Period-over-period windowing for daily metric series.
 *
 * Every metric-history table stores one or more rows per UTC day (a report
 * row and a nightly row can share a day). Dashboard consumers collapse to
 * one row per day, then aggregate the current N-day window against the
 * previous N days. Pure functions, no DB imports — exported through
 * `@repo/core/client` for the brand dashboard's L1 cards.
 */

export type WindowMode = "mean" | "sum" | "last";

export interface WindowDelta {
  /** Aggregate over the current window; null when the window has no rows. */
  current: number | null;
  /**
   * Aggregate over the previous window; null when it has no rows. Sparse
   * report-only properties land here — render "—", never a fake delta.
   */
  previous: number | null;
  /** current − previous; null unless both windows have data. */
  delta: number | null;
  /** Relative change in percent; null when previous is 0 or missing. */
  deltaPct: number | null;
}

const dayKey = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * One row per UTC day; when several rows share a day (report + nightly),
 * the latest measurement wins, with later array position breaking exact
 * timestamp ties. Returns rows sorted oldest → newest.
 */
export function collapseDaily<T>(
  rows: readonly T[],
  getDate: (row: T) => Date | string,
): T[] {
  const byDay = new Map<string, { row: T; ts: number }>();
  for (const row of rows) {
    const date = new Date(getDate(row));
    const ts = date.getTime();
    if (Number.isNaN(ts)) continue;
    const key = dayKey(date);
    const existing = byDay.get(key);
    if (!existing || ts >= existing.ts) {
      byDay.set(key, { row, ts });
    }
  }
  return [...byDay.values()]
    .sort((a, b) => a.ts - b.ts)
    .map((entry) => entry.row);
}

export interface AggregateWindowOptions<T> {
  getDate: (row: T) => Date | string;
  getValue: (row: T) => number | null | undefined;
  /** Window length in days: current = last N days, previous = the N before. */
  days?: number;
  /** How rows combine within a window. SoV-style rates use "mean" (matches
   *  the sparkline the user sees); counts use "sum". */
  mode?: WindowMode;
  /** Injectable clock for tests. */
  now?: Date;
}

/**
 * Collapse a daily series and compare the last `days` days against the
 * `days` before them. Rows dated after `now` are ignored; rows with a
 * null/NaN value are skipped.
 */
export function aggregateWindowDelta<T>(
  rows: readonly T[],
  {
    getDate,
    getValue,
    days = 7,
    mode = "mean",
    now = new Date(),
  }: AggregateWindowOptions<T>,
): WindowDelta {
  const daily = collapseDaily(rows, getDate);
  const end = now.getTime();
  const windowMs = days * 24 * 60 * 60 * 1000;
  const currentStart = end - windowMs;
  const previousStart = end - 2 * windowMs;

  const currentValues: number[] = [];
  const previousValues: number[] = [];
  for (const row of daily) {
    const ts = new Date(getDate(row)).getTime();
    if (ts > end) continue;
    const value = getValue(row);
    if (value == null || Number.isNaN(value)) continue;
    if (ts > currentStart) {
      currentValues.push(value);
    } else if (ts > previousStart) {
      previousValues.push(value);
    }
  }

  const aggregate = (values: number[]): number | null => {
    if (values.length === 0) return null;
    switch (mode) {
      case "sum":
        return values.reduce((a, b) => a + b, 0);
      case "last":
        return values[values.length - 1];
      default:
        return values.reduce((a, b) => a + b, 0) / values.length;
    }
  };

  const current = aggregate(currentValues);
  const previous = aggregate(previousValues);
  const delta =
    current != null && previous != null ? current - previous : null;
  const deltaPct =
    delta != null && previous ? (delta / Math.abs(previous)) * 100 : null;

  return { current, previous, delta, deltaPct };
}
